'use client';

import DatePickerButton from '@/components/DatePickerButton/DatePickerButton';
import {
  maskGermanDateInput,
  toGermanDisplayDateValue,
  toHtmlDateFieldValue,
  toStoredDateValue,
} from '@/components/Form/Fields/DateField/dateFieldIso';
import { TextField, type TextFieldProps } from '@/components/textField';
import useForkRef from '@/helper/react/useForkRef';
import type * as React from 'react';
import { useRef, useState } from 'react';

import styles from './DateField.module.scss';

export type IsoDateFieldProps = Omit<TextFieldProps, 'type' | 'value'> & {
  /** ISO `YYYY-MM-DD`, German `DD.MM.YYYY`, `Date`, or empty. */
  value: string | Date | '' | null | undefined;
};

type NativeDateFieldControlProps = {
  normalizedValue: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

/** Keeps the committed ISO value while a native date segment is edited (e.g. year). */
function useNativeDateFieldControl({ normalizedValue, onChange, onFocus, onBlur }: NativeDateFieldControlProps) {
  const [focused, setFocused] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setFocused(true);
    setDraftValue(normalizedValue);
    onFocus?.(e);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const next = e.target.value;
    setDraftValue(next);
    if (!next && normalizedValue) {
      return;
    }
    onChange?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setFocused(false);
    if (!e.target.value && normalizedValue) {
      setDraftValue(normalizedValue);
    }
    onBlur?.(e);
  };

  return {
    fieldValue: focused ? draftValue : normalizedValue,
    handleBlur,
    handleChange,
    handleFocus,
  };
}

/** Always renders a native date input and stores ISO `YYYY-MM-DD`. */
export const NativeIsoDateField: React.FC<IsoDateFieldProps> = ({ value, onChange, onFocus, onBlur, ...props }) => {
  const normalizedValue = toHtmlDateFieldValue(value);
  const { fieldValue, handleBlur, handleChange, handleFocus } = useNativeDateFieldControl({
    normalizedValue,
    onBlur,
    onChange,
    onFocus,
  });

  return (
    <TextField
      {...props}
      type="date"
      value={fieldValue}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
    />
  );
};

NativeIsoDateField.displayName = 'NativeIsoDateField';

/** Always renders a native date input with the custom desktop calendar button. */
export const CalendarIsoDateField: React.FC<IsoDateFieldProps> = ({
  value,
  InputProps: inputPropsOuter,
  slotProps: slotPropsOuter,
  inputRef: inputRefProp,
  onChange,
  onFocus,
  onBlur,
  ...props
}) => {
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const mergedInputRef = useForkRef(pickerInputRef, inputRefProp);
  const normalizedValue = toHtmlDateFieldValue(value);
  const { fieldValue, handleBlur, handleChange, handleFocus } = useNativeDateFieldControl({
    normalizedValue,
    onBlur,
    onChange,
    onFocus,
  });
  const inputSlot = slotPropsOuter?.input as Record<string, unknown> | undefined;
  const endFromSlot = inputSlot?.endAdornment as React.ReactNode | undefined;
  const inputPropsRecord = inputPropsOuter as Record<string, unknown> | undefined;
  const endFromInputProps = inputPropsRecord?.endAdornment as React.ReactNode | undefined;
  const userEndAdornment = endFromSlot ?? endFromInputProps;
  const { endAdornment: _dropInputPropsEnd, ...inputPropsWithoutEnd } = inputPropsRecord ?? {};
  const { input: slotInputRaw, ...slotPropsWithoutInput } = slotPropsOuter ?? {};
  const slotInputObj = slotInputRaw as Record<string, unknown> | undefined;
  const { endAdornment: _dropSlotEnd, ...slotInputWithoutEnd } = slotInputObj ?? {};

  return (
    <TextField
      {...props}
      InputProps={inputPropsWithoutEnd}
      inputRef={mergedInputRef ?? pickerInputRef}
      slotProps={{
        ...slotPropsWithoutInput,
        input: {
          ...slotInputWithoutEnd,
          endAdornment: (
            <div className={styles.adornmentRow}>
              <DatePickerButton ref={pickerInputRef as React.RefObject<HTMLInputElement>} />
              {userEndAdornment}
            </div>
          ),
        },
      }}
      type="date"
      value={fieldValue}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
    />
  );
};

CalendarIsoDateField.displayName = 'CalendarIsoDateField';

/** Renders a German `DD.MM.YYYY` text input while storing ISO `YYYY-MM-DD`. */
export const MaskedIsoDateField: React.FC<IsoDateFieldProps> = ({ value, inputProps, ...props }) => {
  const [focused, setFocused] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const displayFromValue = toGermanDisplayDateValue(value);
  const { onFocus: onFocusProp, onChange: onChangeProp, onBlur: onBlurProp, ...restForField } = props;

  const emitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, maskedDisplay: string) => {
    if (!onChangeProp) return;
    const storedValue = toStoredDateValue(maskedDisplay);
    onChangeProp({
      ...e,
      target: { ...e.target, value: storedValue, name: e.target.name },
      currentTarget: { ...e.currentTarget, value: storedValue, name: e.target.name },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setFocused(true);
    setDraftValue(displayFromValue);
    onFocusProp?.(e);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const masked = maskGermanDateInput(e.target.value, draftValue);
    setDraftValue(masked);
    emitChange(e, masked);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const masked = maskGermanDateInput(draftValue);
    setFocused(false);
    emitChange(e, masked);
    onBlurProp?.(e);
  };

  return (
    <TextField
      {...restForField}
      inputProps={{
        ...inputProps,
        inputMode: 'numeric',
        pattern: '[0-9]*',
      }}
      type="text"
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      value={focused ? draftValue : displayFromValue}
    />
  );
};

MaskedIsoDateField.displayName = 'MaskedIsoDateField';

/** Native date input with calendar button. Use {@link IsoDateFieldWithoutCalendarButton} for masked entry without a picker (e.g. birthday). */
export const IsoDateField: React.FC<IsoDateFieldProps> = (props) => <CalendarIsoDateField {...props} />;

IsoDateField.displayName = 'IsoDateField';

/** Always renders the masked German date input without a calendar button. */
export const IsoDateFieldWithoutCalendarButton: React.FC<IsoDateFieldProps> = (props) => (
  <MaskedIsoDateField {...props} />
);

IsoDateFieldWithoutCalendarButton.displayName = 'IsoDateFieldWithoutCalendarButton';
